/**
 * Milk sales tickets: the calls, their shapes and their cache.
 *
 * In a module of its own, as the road closures are (`closures.ts`), so screens that mock `api` in their
 * tests are not changed by it; the transport is the same `request`, so the token, the envelope and the
 * sign-in redirect stay in one place.
 *
 * Shapes read from `api/app/schemas/milk_sales.py` (4 October 2026). Every time carries its offset, in the
 * planner's zone, which the held list sends as `time_zone`; every date is `YYYY-MM-DD`. Before the
 * database has the milk sales tables, or this application its access to them, every call is refused with
 * 503 `MILK_SALES_NOT_AVAILABLE`, and the screen says so in words.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { addDays } from "../time";
import { request } from "./client";

export interface TicketMail {
  subject: string | null;
  /** Opens the mail, with the scan attached, in Outlook on the web. */
  web_link: string | null;
  sender: string | null;
}

export interface ProposedLine {
  producer_id: number | null;
  farm_as_read: string | null;
  /** The haulier's Optiload code for the farm; `code_known` when the planner has it. */
  location_code: string | null;
  code_known: boolean;
  collected_on: string | null;
  collected_at: string | null;
  litres: number | null;
  ticket_number: string | null;
  vehicle_registration: string | null;
}

export interface HeldImport {
  import_id: number;
  received_at: string;
  attachment_name: string;
  hauler: string | null;
  method: string;
  /** Why the morning job did not record it, in the job's words. */
  problems: string[];
  mail: TicketMail | null;
  proposed: {
    buyer: string | null;
    /** Null when no transfer into the buyer's tanker was read. */
    load_date: string | null;
    delivery_note_litres: number | null;
    lines: ProposedLine[];
  };
  summaries: {
    truck: string | null;
    route: string | null;
    milk_collected: number | null;
    milk_onboard: number | null;
    transfers: { tank: string | null; litres: number | null; at: string | null }[];
  }[];
  deliveries: { litres: number | null; tank: string | null }[];
  /** The text the OCR read, for checking against the scan. */
  text: string | null;
  /** A scan from the MilkSupply site instead of a mail (MR-2). */
  scan?: { name: string | null; web_url: string | null } | null;
  /** The scan's run was unloaded at Gwalchmai: confirming records the collections, and no sold load. */
  to_plant?: boolean;
}

export interface HeldList {
  time_zone: string;
  imports: HeldImport[];
}

export interface ConfirmLine {
  producer_id: number;
  collected_on: string;
  collected_at: string | null;
  litres: number;
  ticket_number: string | null;
  vehicle_registration: string | null;
  location_code: string | null;
}

export interface ConfirmBody {
  load_date: string;
  lines: ConfirmLine[];
  /** Codes the haulier's table did not know, remembered for the next tickets. */
  map_codes: { code: string; producer_id: number }[];
  /** What was corrected and why; kept with the decision (decision_note). */
  note: string | null;
}

export interface Confirmed {
  import_id: number;
  status: "confirmed";
  /** Null for a run unloaded at Gwalchmai (a scan from the MilkSupply site): nothing was sold. */
  sold_load_id: number | null;
  litres: number;
  codes_remembered: string[];
}

export interface Rejected {
  import_id: number;
  status: "rejected";
  /** The reason as stored with the decision. */
  decision_note: string;
}

export interface RejectedImport {
  import_id: number;
  received_at: string;
  attachment_name: string;
  hauler: string | null;
  mail: TicketMail | null;
  decided_by: string | null;
  decided_at: string;
  /** Why it was rejected, in the person's words. */
  decision_note: string | null;
}

export interface RejectedList {
  time_zone: string;
  from: string;
  to: string;
  imports: RejectedImport[];
}

export interface Buyer {
  buyer_id: number;
  name: string;
}

export interface Contract {
  contract_id: number;
  buyer: string;
  contract_number: string;
  confirmed_on: string;
  period_from: string;
  period_to: string;
  loads_agreed: number | null;
  litres_per_load: number | null;
  price_gbp_per_litre: number;
  price_basis: string;
  payment_terms: string;
  document_ref: string | null;
  notes: string | null;
  loads_recorded: number;
  litres_recorded: number;
  /** Who recorded it; null for one recorded by a script. */
  created_by: string | null;
  /** Who checked it against its filed document; null until someone has. */
  checked_by: string | null;
  /** When, with the planner's zone's offset; null until checked. */
  checked_at: string | null;
}

export interface NewContract {
  buyer_id: number;
  contract_number: string;
  confirmed_on: string;
  period_from: string;
  period_to: string;
  loads_agreed: number | null;
  litres_per_load: number | null;
  price_gbp_per_litre: number;
  price_basis: string;
  payment_terms: string;
  document_ref: string | null;
  notes: string | null;
}

export interface SoldLoadLine {
  producer_id: number;
  farm_name: string;
  producer_number: number | null;
  collected_on: string;
  collected_at: string | null;
  litres: number;
  ticket_number: string | null;
  vehicle_registration: string | null;
  source: string;
}

export interface SoldLoad {
  sold_load_id: number;
  buyer: string;
  load_date: string;
  contract_number: string | null;
  /** What is sold: the haulier's Optiload transfers into the buyer's tanker; null until recorded. */
  delivery_note_litres: number | null;
  /** The farms' whole collections on the load's day (the API's sum). Not the quantity sold. */
  litres: number;
  /** accepted: read unattended; confirmed: by a person here; null: entered another way. */
  import_status: string | null;
  decided_by: string | null;
  decided_at: string | null;
  /** The confirming person's note, when they gave one. */
  decision_note: string | null;
  lines: SoldLoadLine[];
}

export interface LoadList {
  time_zone: string;
  from: string;
  to: string;
  loads: SoldLoad[];
}

/** A contract a waiting load may be linked to: its own, or the buyer's whose period holds the load's day. No price. */
export interface WaitingContract {
  sales_contract_id: number;
  contract_number: string;
  period_from: string;
  period_to: string;
}

/** A load whose delivery note waits: for its contract (none was named) or for the buyer's litres (the D600). Until
 *  it is linked it is not invoiced. */
export interface WaitingLoad {
  sold_load_id: number;
  buyer: string;
  load_date: string;
  /** The farms' whole collections on the load. Not the quantity sold. */
  litres: number;
  waits_for: "contract" | "litres";
  contract_number: string | null;
  delivery_note_litres: number | null;
  contracts: WaitingContract[];
}

export interface LinkLoadBody {
  sales_contract_id: number;
  /** The buyer's litres, when the load has none yet. */
  delivery_note_litres?: number;
}

export interface LinkedLoad {
  sold_load_id: number;
  contract_number: string | null;
  delivery_note_litres: number | null;
  /** True once the load has its contract and its litres: it no longer waits. */
  complete: boolean;
}

export interface Producer {
  producer_id: number;
  farm_name: string;
  producer_number: number | null;
  status: string;
}

/** Sales offers (bsd-knowledge v0.126, spec 2026-10-14-sales-offers-before-contract-design.md): what we offer
 *  to sell before the buyer's confirmation. Shapes read from `api/app/schemas/milk_sales.py` (14 October 2026). */
export type OfferStatus = "proposed" | "offered" | "agreed" | "contracted" | "withdrawn";

export interface OfferAdvice {
  /** confirmed: the newest confirmed run; proposal: the offer's own run; none: no run covers the day. */
  source: "confirmed" | "proposal" | "none";
  run_id: number | null;
  barrels: number;
  /** Where the advice comes from, for the screen; not part of the text to copy. */
  label: string;
  /** Plain lines for the haulier; the litres are the plan's forecast. */
  text: string;
}

export interface OfferDay {
  loading_date: string;
  loads: number;
  /** Loads × litres per load: the agreement, not a measurement. */
  litres: number;
  /** The D600 litres of the buyer's loads recorded that day; null for a load without its delivery note yet. */
  sold_litres: (number | null)[];
  /** Only for an agreed or contracted offer. */
  advice: OfferAdvice | null;
}

export interface OfferStep {
  status: string;
  stepped_at: string;
  /** Null: the planner job. */
  stepped_by: string | null;
  mail_ref: string | null;
  note: string | null;
}

export interface OfferFit {
  state: "pending" | "fits" | "short";
  days: { day: string; barrels_planned: number; loads: number; short_litres: number; sentence: string }[];
}

export interface LinkableContract {
  contract_id: number;
  buyer_id: number;
  contract_number: string;
  period_from: string;
  period_to: string;
}

/** ERPNext's status of the quotation behind an offer (bsd-knowledge v0.211); before it, derived from status. */
export type QuotationStatus = "draft" | "open" | "ordered" | "lost" | "cancelled";

/** The sales order made from an offer: a draft from the buyer's yes, submitted with the buyer's confirmation. */
export interface OfferOrder {
  name: string;
  status: "draft" | "submitted";
  /** The buyer's contract number, once the confirmation is in. */
  contract_number: string | null;
  checked: boolean;
}

export interface Offer {
  offer_id: number;
  buyer_id: number;
  buyer: string;
  period_from: string;
  period_to: string;
  /** The planner's words, kept for the Week plan. */
  status: OfferStatus;
  quotation_status: QuotationStatus;
  /** SAL-QTN-...; null before v0.211. */
  quotation_name: string | null;
  amended_from: string | null;
  /** The quotation that amends this cancelled one; Amend is then done. */
  amended_as: string | null;
  order: OfferOrder | null;
  lost_reasons: string[];
  competitors: string[];
  detailed_reason: string | null;
  litres_per_load: number;
  location: string | null;
  price_gbp_per_litre: number | null;
  price_basis: string | null;
  planning_run_id: number | null;
  sales_contract_id: number | null;
  contract_number: string | null;
  notes: string | null;
  loads: number;
  litres: number;
  days: OfferDay[];
  steps: OfferStep[];
  /** The days in the form of every sales mail, ready to copy; a person sends it. */
  mail_text: string;
  fit: OfferFit;
  /** For an agreed offer: its buyer's contracts in the period that no offer is linked to. */
  linkable_contracts: LinkableContract[];
}

export interface OfferList {
  time_zone: string;
  today: string;
  from: string;
  to: string;
  /** trading or admin; the others read. */
  can_write: boolean;
  /** The offers are ERPNext quotations (v0.211): Set as lost and Amend need it. */
  sales_documents: boolean;
  /** The Set as lost dialog's lists; empty before v0.211. */
  lost_reasons: string[];
  competitors: string[];
  offers: Offer[];
}

export interface ContractList {
  /** payments_admin, admin or trading enter a contract; management reads. */
  can_write: boolean;
  /** The check against the filed document is the milk office's (C1): payments_admin or admin. */
  can_check: boolean;
  contracts: Contract[];
}

export interface OfferDayBody {
  loading_date: string;
  loads: number;
}

export interface NewOffer {
  buyer_id: number;
  period_from: string;
  period_to: string;
  litres_per_load: number;
  days: OfferDayBody[];
  notes: string | null;
}

/** ERPNext's Quotation actions (design 2026-10-28-offer-actions-like-erpnext, OA-1 to OA-5). */
export type OfferAction =
  | { action: "submit"; body: { mail_ref?: string | null } }
  | { action: "make-sales-order"; body: { price_gbp_per_litre?: number | null; price_basis?: string | null; mail_ref?: string | null } }
  | { action: "set-as-lost"; body: OfferLostBody }
  | { action: "cancel"; body: { note?: string | null; mail_ref?: string | null } }
  | { action: "amend"; body?: undefined };

export interface OfferLostBody {
  lost_reasons: string[];
  competitors: string[];
  detailed_reason: string | null;
  mail_ref: string | null;
}

export interface OfferPriceBody {
  price_gbp_per_litre: number;
  price_basis: string | null;
  mail_ref: string | null;
}

export interface OfferWritten {
  offer_id: number;
  status: string;
}

/** The week plan per order on Milk sales (DM-3): loads and litres only, read from the shared view. */
export interface SalesWeekDay {
  day: string;
  loads_planned: number;
  /** loads x litres per load; null for a contract without litres per load. */
  litres_planned: number | null;
  loads_delivered: number;
  litres_delivered: number;
  loads_without_litres: number;
  plan_notes: string | null;
}

export interface SalesWeekOrder {
  buyer_id: number;
  buyer: string;
  /** Null: the loads sold without a contract. */
  sales_contract_id: number | null;
  contract_number: string | null;
  /** Set: an offer the buyer agreed to, not yet contracted (sales_contract_id is then null too). */
  sales_offer_id?: number | null;
  days: SalesWeekDay[];
}

export interface SalesWeekTotalDay {
  day: string;
  loads_planned: number;
  loads_delivered: number;
  litres_delivered: number;
  /** Delivered loads whose delivery note has no litres yet: the litres above are short of them. */
  loads_without_litres: number;
}

export interface SalesWeekTotal {
  /** Null: over every buyer shown. */
  buyer_id: number | null;
  buyer: string | null;
  days: SalesWeekTotalDay[];
  loads_planned: number;
  loads_delivered: number;
  litres_delivered: number;
  loads_without_litres: number;
}

export interface SalesWeekPlan {
  time_zone: string;
  week: string;
  today: string;
  buyers: { buyer_id: number; name: string }[];
  orders: SalesWeekOrder[];
  totals: SalesWeekTotal[];
}

/** An offer still in play, in loads and litres: no price, no mail, no history (DM-3). */
export interface OfferPlan {
  offer_id: number;
  buyer: string;
  period_from: string;
  period_to: string;
  status: OfferStatus;
  location: string | null;
  contract_number: string | null;
  loads: number;
  litres: number;
  days: { loading_date: string; loads: number; litres: number }[];
}

export interface OfferPlanList {
  time_zone: string;
  today: string;
  from: string;
  to: string;
  offers: OfferPlan[];
}

export const milkSalesApi = {
  /** Without a week: this week in the planner's zone. */
  week: (week?: string, buyer?: number) =>
    request<SalesWeekPlan>("/api/milk-sales/week", { week, buyer: buyer === undefined ? undefined : String(buyer) }),
  offersPlan: () => request<OfferPlanList>("/api/milk-sales/offers/plan"),
  held: () => request<HeldList>("/api/milk-sales/imports"),
  confirm: (importId: number, body: ConfirmBody) =>
    request<Confirmed>(`/api/milk-sales/imports/${importId}/confirm`, {}, { method: "POST", body }),
  reject: (importId: number, reason: string) =>
    request<Rejected>(`/api/milk-sales/imports/${importId}/reject`, {}, { method: "POST", body: { reason } }),
  buyers: () => request<{ buyers: Buyer[] }>("/api/milk-sales/buyers"),
  contracts: () => request<ContractList>("/api/milk-sales/contracts"),
  createContract: (contract: NewContract) =>
    request<Contract>("/api/milk-sales/contracts", {}, { method: "POST", body: contract }),
  checkContract: (contractId: number) =>
    request<Contract>(`/api/milk-sales/contracts/${contractId}/check`, {}, { method: "POST", body: {} }),
  loads: () => request<LoadList>("/api/milk-sales/loads"),
  waitingLoads: () => request<{ loads: WaitingLoad[] }>("/api/milk-sales/waiting-loads"),
  linkLoad: (loadId: number, body: LinkLoadBody) =>
    request<LinkedLoad>(`/api/milk-sales/loads/${loadId}/link`, {}, { method: "POST", body }),
  rejected: () => request<RejectedList>("/api/milk-sales/rejected"),
  producers: () => request<{ producers: Producer[] }>("/api/milk-sales/producers"),
  /** Without a range: this week and the next in the planner's zone, which only the API knows. */
  offers: (from?: string, to?: string) => request<OfferList>("/api/milk-sales/offers", { from, to }),
  createOffer: (offer: NewOffer) => request<OfferWritten>("/api/milk-sales/offers", {}, { method: "POST", body: offer }),
  offerDays: (offerId: number, days: OfferDayBody[]) =>
    request<OfferWritten>(`/api/milk-sales/offers/${offerId}/days`, {}, { method: "PUT", body: { days } }),
  offerAction: (offerId: number, a: OfferAction) =>
    request<OfferWritten>(`/api/milk-sales/offers/${offerId}/${a.action}`, {}, { method: "POST", body: a.body ?? {} }),
  offerPrice: (offerId: number, body: OfferPriceBody) =>
    request<OfferWritten>(`/api/milk-sales/offers/${offerId}/update-price`, {}, { method: "POST", body }),
  linkOffer: (offerId: number, contractId: number) =>
    request<OfferWritten>(`/api/milk-sales/offers/${offerId}/link`, {}, {
      method: "POST",
      body: { sales_contract_id: contractId },
    }),
};

/** Under their own first word: a decision invalidates the held list, the loads and the contracts'
 *  counts at once. */
export const milkSalesKeys = {
  all: () => ["milk-sales"] as const,
  held: () => ["milk-sales", "held"] as const,
  buyers: () => ["milk-sales", "buyers"] as const,
  contracts: () => ["milk-sales", "contracts"] as const,
  loads: () => ["milk-sales", "loads"] as const,
  waiting: () => ["milk-sales", "waiting"] as const,
  rejected: () => ["milk-sales", "rejected"] as const,
  producers: () => ["milk-sales", "producers"] as const,
  offers: (monday: string | null) => ["milk-sales", "offers", monday ?? "this week"] as const,
  week: (monday: string | null, buyer: number | null) =>
    ["milk-sales", "week", monday ?? "this week", buyer ?? "all"] as const,
  offersPlan: () => ["milk-sales", "offers-plan"] as const,
};

/** The week plan from ``monday`` (null: this week), for one buyer or all. */
export function useWeekPlan(monday: string | null, buyer: number | null) {
  return useQuery({
    queryKey: milkSalesKeys.week(monday, buyer),
    queryFn: () => milkSalesApi.week(monday ?? undefined, buyer ?? undefined),
  });
}

/** The offers still in play, in litres: this week and the next. */
export function useOffersPlan() {
  return useQuery({ queryKey: milkSalesKeys.offersPlan(), queryFn: () => milkSalesApi.offersPlan() });
}

export function useHeldImports() {
  return useQuery({ queryKey: milkSalesKeys.held(), queryFn: () => milkSalesApi.held() });
}

export function useBuyers() {
  return useQuery({ queryKey: milkSalesKeys.buyers(), queryFn: () => milkSalesApi.buyers() });
}

export function useContracts() {
  return useQuery({ queryKey: milkSalesKeys.contracts(), queryFn: () => milkSalesApi.contracts() });
}

export function useSoldLoads() {
  return useQuery({ queryKey: milkSalesKeys.loads(), queryFn: () => milkSalesApi.loads() });
}

export function useWaitingLoads() {
  return useQuery({ queryKey: milkSalesKeys.waiting(), queryFn: () => milkSalesApi.waitingLoads() });
}

export function useRejectedImports() {
  return useQuery({ queryKey: milkSalesKeys.rejected(), queryFn: () => milkSalesApi.rejected() });
}

export function useProducers() {
  return useQuery({ queryKey: milkSalesKeys.producers(), queryFn: () => milkSalesApi.producers() });
}

function useMilkSalesWrite<A, R>(write: (args: A) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    // Refetched on a refusal too: a 409 means the list on screen is old. Not returned, so the
    // caller's own onSuccess is not held until the refetch lands.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: milkSalesKeys.all() });
    },
  });
}

export function useConfirmImport() {
  return useMilkSalesWrite(({ importId, body }: { importId: number; body: ConfirmBody }) =>
    milkSalesApi.confirm(importId, body),
  );
}

export function useRejectImport() {
  return useMilkSalesWrite(({ importId, reason }: { importId: number; reason: string }) =>
    milkSalesApi.reject(importId, reason),
  );
}

export function useRecordContract() {
  return useMilkSalesWrite((contract: NewContract) => milkSalesApi.createContract(contract));
}

/** Name a waiting load's contract, with the buyer's litres when it has none. */
export function useLinkLoad() {
  return useMilkSalesWrite(({ loadId, body }: { loadId: number; body: LinkLoadBody }) =>
    milkSalesApi.linkLoad(loadId, body),
  );
}

/** The confirm step: the caller has checked the contract against its document. */
export function useCheckContract() {
  return useMilkSalesWrite((contractId: number) => milkSalesApi.checkContract(contractId));
}

/** The offers of two weeks from ``monday``, Monday to Sunday; null: this week and the next. */
export function useOffers(monday: string | null) {
  return useQuery({
    queryKey: milkSalesKeys.offers(monday),
    queryFn: () => (monday ? milkSalesApi.offers(monday, addDays(monday, 13)) : milkSalesApi.offers()),
  });
}

export function useCreateOffer() {
  return useMilkSalesWrite((offer: NewOffer) => milkSalesApi.createOffer(offer));
}

export function useOfferDays() {
  return useMilkSalesWrite(({ offerId, days }: { offerId: number; days: OfferDayBody[] }) =>
    milkSalesApi.offerDays(offerId, days),
  );
}

export function useOfferAction() {
  return useMilkSalesWrite(({ offerId, action }: { offerId: number; action: OfferAction }) =>
    milkSalesApi.offerAction(offerId, action),
  );
}

export function useOfferPrice() {
  return useMilkSalesWrite(({ offerId, body }: { offerId: number; body: OfferPriceBody }) =>
    milkSalesApi.offerPrice(offerId, body),
  );
}

export function useLinkOffer() {
  return useMilkSalesWrite(({ offerId, contractId }: { offerId: number; contractId: number }) =>
    milkSalesApi.linkOffer(offerId, contractId),
  );
}
